import { Button } from '@jotta/ui/Button'
import { Stack } from '@jotta/ui/Stack'
import { useBrandStore } from '@jotta/ui/useBrandTheme'
import { getBrandZIndex } from '@jotta/ui/zIndex'
import { useDebounce, useThrottle } from '@jotta/hooks'
import type { FileActionStatus } from '@jotta/types/FileActions'
import { ProgressBar } from '@jotta/ui/ProgressBar'
import { filesize } from '@jotta/utils/filesize'
import { Trans, plural, t } from '@lingui/macro'
import Debug from 'debug'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useState } from 'react'
import type { FileUploadManager } from '../ActionManager/FileUploadManager'
import { FileActionQueueDetails } from './FileActionQueueDetails'

const CLOSE_AFTER_MS = 5000

function Preparing({ ms = 1000 }: { ms?: number }) {
  const [dots, setDots] = useState('')

  const mutate = useCallback(() => {
    setDots(old => {
      if (old.length >= 3) {
        return ''
      }
      return `${old}.`
    })
  }, [])

  useEffect(() => {
    const timer = window.setInterval(mutate, ms)

    return () => {
      window.clearInterval(timer)
    }
  }, [ms, mutate])

  return <div sx={{ width: '5em' }}>{`${t`Preparing`}${dots}`}</div>
}

export interface FileActionQueueStatusProps {
  manager: FileUploadManager
  actions?: FileActionStatus[]
  showDetails?: boolean
  showDebugInfo?: boolean
}

function useFilter(value: boolean) {
  const [filtered, setFiltered] = useState(value)

  const setThrottled = useThrottle(
    useCallback((val: boolean) => setFiltered(val), []),
    400 * 4,
    { leading: true },
  )

  const setDebounced = useDebounce(
    useCallback((val: boolean) => {
      setFiltered(val)
    }, []),
    2000 * 4,
  )

  useEffect(() => {
    if (value === filtered) {
      return
    }

    if (value) {
      setThrottled(true)
    }
    setDebounced(value)
  }, [value, filtered, setThrottled, setDebounced])

  return filtered
}

const debug = Debug('jotta:publicfiles:FileActionQueueStatus')
export const FileActionQueueStatus = observer<FileActionQueueStatusProps>(
  function FileActionQueueStatus({ manager, showDebugInfo = false }) {
    const brand = useBrandStore()
    const {
      completed,
      hasCompleted,
      hasErrors,
      total,
      totalSize,
      completedSize,
      allAborted,
      hasPendingUploads,
    } = manager
    const autoClose = useDebounce(manager.cancel, CLOSE_AFTER_MS)
    const length = manager.actions.length

    useEffect(() => {
      if (!length) {
        manager.open = false
      }
    }, [manager, length])

    useEffect(() => {
      if (hasCompleted && !hasErrors) {
        autoClose()
      } else {
        autoClose.cancel()
      }
    }, [hasCompleted, autoClose, hasErrors])

    const itemHeight = 40
    const showDone = hasCompleted && !allAborted
    const showCancelled = hasCompleted && allAborted
    const showUploadStatus =
      useFilter(hasPendingUploads) && !showDone && !showCancelled
    const showPreparing = !showUploadStatus && !showDone && !showCancelled

    return (
      <div
        sx={{
          position: 'absolute',
          width: '100%',
          bottom: 0,
          boxShadow: '0 0 8px rgba(0,0,0,0.125)',
        }}
      >
        {manager.detailsViewOpen && (
          <FileActionQueueDetails
            showDebugInfo={showDebugInfo}
            items={manager.actions}
            itemHeight={itemHeight}
          />
        )}

        <Stack
          horizontal
          gap={[2, 3]}
          variant="cards.bottomStatus"
          sx={{
            px: 3,
            height: 'var(--content-bottom)',
            gridTemplateColumns: [
              'max-content 1fr max-content',
              'max-content max-content 1fr max-content',
            ],
            flex: '1 1 auto',
            alignItems: 'center',
            position: 'relative',
            zIndex: getBrandZIndex('appMainBottomToolbarContent'),
            overflowX: 'hidden',
          }}
        >
          <div sx={{ position: 'relative' }}>
            <div sx={{ gridColumn: 0, width: '100%', position: 'absolute' }}>
              {showPreparing && <Preparing />}
              {showDone && <Trans>Done</Trans>}
              {showCancelled && <Trans>Cancelled</Trans>}
              {brand.currentBreakpoint === 0 && `${completed} / ${total}`}
            </div>
            <div
              sx={{
                opacity: showUploadStatus ? 1 : 0,
              }}
            >
              {t({
                id: 'Uploader.uploading',
                message: plural(total, {
                  one: 'Uploading one file',
                  other: `Uploading ${completed} of # files`,
                }),
              })}
            </div>
          </div>
          {brand.currentBreakpoint > 1 ? (
            <div>{`${filesize(completedSize)} / ${filesize(totalSize)}`}</div>
          ) : brand.currentBreakpoint > 0 ? (
            <div>{`${filesize(completedSize, { round: 0 })} / ${filesize(
              totalSize,
              { round: 0 },
            )}`}</div>
          ) : undefined}
          <ProgressBar
            sx={{
              width: '100%',
            }}
            total={totalSize}
            completed={completedSize}
          />
          <Stack horizontal gap={[2, 3]}>
            {hasCompleted ? (
              <Button
                data-testid="upload-complete"
                className="btn btn-primary"
                onClick={manager.cancel}
              >
                <Trans>Done</Trans>
              </Button>
            ) : (
              <>
                <Button
                  className="btn btn-outline"
                  onClick={manager.cancel}
                  px={[2, 4]}
                >
                  <Trans>Cancel</Trans>
                </Button>
                {manager.detailsViewOpen ? (
                  <Button
                    className="btn btn-primary"
                    onClick={() => {
                      manager.detailsViewOpen = false
                    }}
                    px={[2, 4]}
                    sx={{ width: '100px' }}
                  >
                    <Trans>Hide</Trans>
                  </Button>
                ) : (
                  <Button
                    className="btn btn-primary"
                    onClick={() => {
                      manager.detailsViewOpen = true
                    }}
                    px={[2, 4]}
                    sx={{ width: '100px' }}
                  >
                    <Trans>Expand</Trans>
                  </Button>
                )}
              </>
            )}
          </Stack>
        </Stack>
      </div>
    )
  },
)
